import { useState, useEffect, useMemo, type CSSProperties } from 'react'
import type { TrainingProgram, TrainingProgramActivation } from '../../types/api'
import type { TrainingTagDto } from '../../api/trainingBlockApi'
import { BLOCK_TAG_CATEGORIES, flattenTrainingTags } from '../../api/trainingBlockApi'
import { trainingBlockApi } from '../../api'
import { Button } from '../ui'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { trainingProgramActivationApi } from '../../api/trainingProgramActivationApi'
import type { ApiError } from '../../api/client'
import { captureEvent } from '../../posthog'
import { countExercisesInItems, formatItemNames } from './blockItemHelpers'
import { BlockIndexLabel } from './BlockIndexLabel'
import OnDemandAssignComposer from './OnDemandAssignComposer'
import { TagCategoryFilter } from './TagCategoryFilter'
import {
  emptyTagCategoryFilterState,
  matchesTagCategoryFilters,
  type TagCategoryFilterState,
} from './tagCategoryFilterUtils'

const WEEK_PRESETS = [1, 2, 4, 6, 8] as const

function nearestPresetWeeks(weeks: number): number {
  return WEEK_PRESETS.reduce((best, w) =>
    Math.abs(w - weeks) < Math.abs(best - weeks) ? w : best
  )
}

interface Props {
  programs: TrainingProgram[]
  traineeId: number
  initialProgramId?: number
  onClose: () => void
  onAssigned: (activation: TrainingProgramActivation) => void
}

function todayIso(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function addWeeksIso(iso: string, weeks: number): string {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + weeks * 7)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function weeksBetweenIso(startIso: string, endIso: string): number {
  const start = new Date(`${startIso}T12:00:00`)
  const end = new Date(`${endIso}T12:00:00`)
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000)
  return Math.max(1, Math.min(52, Math.round(days / 7)))
}

function programFilterTags(program: TrainingProgram): string[] {
  const tags = new Set<string>(program.tags ?? [])
  program.days.forEach(day => {
    ; (day.blockTags ?? []).forEach(tag => tags.add(tag))
  })
  return Array.from(tags)
}

export default function AssignProgramSheet({ programs, traineeId, initialProgramId, onClose, onAssigned }: Props) {
  const theme = useTheme()
  const { t, translate } = useTranslation()

  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(initialProgramId ?? null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilters, setActiveFilters] = useState<TagCategoryFilterState>(emptyTagCategoryFilterState())
  const [startDate, setStartDate] = useState(todayIso)
  const [weeks, setWeeks] = useState(4)
  const [endDate, setEndDate] = useState(() => addWeeksIso(todayIso(), 4))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [availableTags, setAvailableTags] = useState<TrainingTagDto[]>([])
  const [showOnDemand, setShowOnDemand] = useState(false)

  useEffect(() => {
    trainingBlockApi.getTags().then(g => setAvailableTags(flattenTrainingTags(g))).catch(() => { })
  }, [])

  const tagEmojiMap = useMemo(
    () => Object.fromEntries(availableTags.map(tag => [tag.key, tag.emoji])),
    [availableTags]
  )

  const tagLabelMap = useMemo(
    () => Object.fromEntries(availableTags.map(tag => [tag.key, tag.label])),
    [availableTags]
  )

  const usedTagKeys = useMemo(() => {
    const keys = new Set<string>()
    programs.forEach(p => programFilterTags(p).forEach(tag => keys.add(tag)))
    return keys
  }, [programs])

  const filteredPrograms = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return programs.filter(p => {
      if (!matchesTagCategoryFilters(programFilterTags(p), activeFilters)) return false
      if (!q) return true
      const nameMatch = p.name.toLowerCase().includes(q)
      const tagMatch = programFilterTags(p).some(tag => {
        const label = (t.trainingTags[tag as keyof typeof t.trainingTags] ?? tagLabelMap[tag] ?? tag).toLowerCase()
        return tag.toLowerCase().includes(q) || label.includes(q)
      })
      const blockMatch = p.days.some(d => (d.blockName ?? '').toLowerCase().includes(q))
      return nameMatch || tagMatch || blockMatch
    })
  }, [programs, searchQuery, activeFilters, t, tagLabelMap])

  const selectedProgram = useMemo(
    () => programs.find(p => p.id === selectedProgramId) ?? null,
    [programs, selectedProgramId]
  )

  function handleStartDateChange(value: string) {
    setStartDate(value)
    setEndDate(addWeeksIso(value, weeks))
    setError(null)
  }

  function handleWeeksChange(nextWeeks: number) {
    const clamped = Math.max(1, Math.min(52, nextWeeks))
    setWeeks(clamped)
    setEndDate(addWeeksIso(startDate, clamped))
    setError(null)
  }

  function handleEndDateChange(value: string) {
    if (value < startDate) {
      setEndDate(value)
      setError(t.trainingPrograms.assignProgram.endBeforeStart)
      return
    }
    setError(null)
    const snapped = nearestPresetWeeks(weeksBetweenIso(startDate, value))
    setWeeks(snapped)
    setEndDate(addWeeksIso(startDate, snapped))
  }

  async function handleAssign() {
    if (!selectedProgramId) return
    if (endDate < startDate) {
      setError(t.trainingPrograms.assignProgram.endBeforeStart)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const activation = await trainingProgramActivationApi.assignToTrainee(selectedProgramId, {
        traineeId,
        durationWeeks: weeks,
        startDate,
      })
      captureEvent('program_assigned', {
        program_id: selectedProgramId,
        trainee_id: traineeId,
        weeks,
      })
      onAssigned(activation)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.trainingPrograms.assignProgram.error.assignFailed)
    } finally {
      setLoading(false)
    }
  }

  const sectionLabel: CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.text.muted,
    marginBottom: 12,
  }

  if (showOnDemand) {
    return (
      <OnDemandAssignComposer
        traineeId={traineeId}
        onClose={() => setShowOnDemand(false)}
        onAssigned={onAssigned}
      />
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: theme.bgColor }}
    >
      <div
        className="flex items-center gap-3 px-4 py-3 shrink-0"
        style={{ borderBottom: `1px solid ${theme.cardBorder}` }}
      >
        <button
          type="button"
          onClick={onClose}
          className="p-2 -ml-2 rounded-xl"
          style={{ color: theme.text.primary }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h2 className="text-lg font-semibold" style={{ color: theme.text.primary }}>
          {t.trainingPrograms.assignProgram.title}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5">
        <button
          type="button"
          onClick={() => setShowOnDemand(true)}
          className="w-full mb-5 rounded-2xl px-4 py-3.5 text-left flex items-center gap-3"
          style={{
            backgroundColor: `${theme.buttonColor}10`,
            border: `1.5px dashed ${theme.buttonColor}50`,
          }}
        >
          <span
            className="w-9 h-9 rounded-xl flex items-center justify-center text-lg font-bold shrink-0"
            style={{ backgroundColor: `${theme.buttonColor}18`, color: theme.accent.text }}
          >
            +
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold" style={{ color: theme.text.primary }}>
              {t.trainingPrograms.assignProgram.onDemand.cta}
            </p>
            <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
              {t.trainingPrograms.assignProgram.onDemand.ctaHint}
            </p>
          </div>
        </button>

        {programs.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: theme.text.muted }}>
            {t.trainingPrograms.assignProgram.noPrograms}
          </p>
        ) : (
          <>
            {/* Search */}
            <div className="relative mb-3">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.text.muted }}>
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t.trainingPrograms.assignProgram.searchPlaceholder}
                className="w-full rounded-xl pl-10 pr-4 py-3 text-base outline-none"
                style={{
                  backgroundColor: theme.inputBg,
                  color: theme.text.primary,
                  border: `1px solid ${theme.inputBorder}`,
                }}
              />
            </div>

            {usedTagKeys.size > 0 && (
              <div className="mb-4">
                <TagCategoryFilter
                  tags={availableTags}
                  usedTagKeys={usedTagKeys}
                  categories={[...BLOCK_TAG_CATEGORIES]}
                  value={activeFilters}
                  onChange={setActiveFilters}
                  bleed={false}
                  defaultExpanded={false}
                />
              </div>
            )}

            <p style={sectionLabel}>{t.trainingPrograms.assignProgram.selectProgram}</p>

            {filteredPrograms.length === 0 ? (
              <p className="text-sm text-center py-6 mb-6" style={{ color: theme.text.muted }}>
                {t.trainingPrograms.assignProgram.noSearchResults}
              </p>
            ) : (
              <div className="flex flex-col gap-2 mb-8">
                {filteredPrograms.map(p => {
                  const isSelected = selectedProgramId === p.id
                  return (
                    <div
                      key={p.id}
                      className="rounded-2xl overflow-hidden transition-colors"
                      style={{
                        backgroundColor: isSelected ? `${theme.buttonColor}12` : theme.cardBg,
                        border: isSelected
                          ? `2px solid ${theme.buttonColor}`
                          : `1px solid ${theme.cardBorder}`,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedProgramId(isSelected ? null : p.id)}
                        className="w-full flex items-center gap-3 px-4 py-3.5 text-left min-h-[52px]"
                      >
                        <div
                          className="w-5 h-5 rounded-full shrink-0 border-2 flex items-center justify-center"
                          style={{
                            borderColor: isSelected ? theme.buttonColor : theme.cardBorder,
                            backgroundColor: isSelected ? theme.buttonColor : 'transparent',
                          }}
                        >
                          {isSelected && (
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                              <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate" style={{ color: theme.text.primary }}>
                            {p.name}
                          </p>
                          <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
                            {t.trainingPrograms.blockCount_other.replace('{{count}}', String(p.days.length))}
                          </p>
                        </div>
                        {isSelected && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16" height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="shrink-0"
                            style={{ color: theme.text.muted, transform: 'rotate(180deg)' }}
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        )}
                      </button>

                      {isSelected && p.days.length > 0 && (
                        <div style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
                          {p.days.map((day, idx) => {
                            const tags = day.blockTags ?? []
                            const dayItems = day.items ?? []
                            const exerciseCount = countExercisesInItems(dayItems)
                            const isLast = idx === p.days.length - 1
                            return (
                              <div
                                key={day.id}
                                className="flex items-start gap-3 px-4 py-2.5"
                                style={{ borderBottom: isLast ? undefined : `1px solid ${theme.cardBorder}` }}
                              >
                                <div className="flex-1 min-w-0">
                                  <BlockIndexLabel index={day.sortOrder + 1} className="block mb-0.5" />
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                                      {day.blockName ?? `Block #${day.blockId}`}
                                    </p>
                                    {exerciseCount > 0 && (
                                      <span className="text-xs" style={{ color: theme.text.muted }}>
                                        {t.trainingBlocks.exerciseCount_other.replace('{{count}}', String(exerciseCount))}
                                      </span>
                                    )}
                                  </div>
                                  {tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {tags.slice(0, 4).map(tag => (
                                        <span
                                          key={tag}
                                          className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full"
                                          style={{
                                            backgroundColor: `${theme.buttonColor}12`,
                                            border: `1px solid ${theme.buttonColor}25`,
                                            color: theme.accent.text,
                                          }}
                                        >
                                          {tagEmojiMap[tag] && <span className="leading-none">{tagEmojiMap[tag]}</span>}
                                          <span>{t.trainingTags[tag as keyof typeof t.trainingTags] ?? tagLabelMap[tag] ?? tag}</span>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {exerciseCount > 0 && (
                                    <ul className="mt-1.5 space-y-0.5">
                                      {dayItems.map(item => (
                                        <li key={item.id} className="flex items-baseline gap-1.5 text-xs" style={{ color: theme.text.secondary }}>
                                          <span className="opacity-50">•</span>
                                          <span>{formatItemNames(item)}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {/* Schedule */}
            <p style={sectionLabel}>{t.trainingPrograms.assignProgram.scheduleLabel}</p>
            <div
              className="rounded-2xl overflow-hidden mb-4"
              style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
            >
              <div
                className="flex items-center justify-between px-4 py-3.5"
                style={{ borderBottom: `1px solid ${theme.cardBorder}` }}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${theme.buttonColor}20`, color: theme.accent.text }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </span>
                  <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                    {t.trainingPrograms.assignProgram.startDate}
                  </p>
                </div>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => handleStartDateChange(e.target.value)}
                  className="text-sm font-semibold bg-transparent border-none outline-none shrink-0"
                  style={{ color: theme.text.primary, colorScheme: theme.isDark ? 'dark' : 'light' }}
                />
              </div>
              <div className="flex items-center justify-between px-4 py-3.5">
                <div className="flex items-center gap-3">
                  <span
                    className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${theme.buttonColor}20`, color: theme.accent.text }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </span>
                  <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                    {t.trainingPrograms.assignProgram.endDate}
                  </p>
                </div>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={e => handleEndDateChange(e.target.value)}
                  className="text-sm font-semibold bg-transparent border-none outline-none shrink-0"
                  style={{ color: theme.text.primary, colorScheme: theme.isDark ? 'dark' : 'light' }}
                />
              </div>
            </div>

            <p style={{ ...sectionLabel, marginBottom: 8 }}>{t.trainingPrograms.assignProgram.quickDuration}</p>
            <div className="flex gap-2 mb-6">
              {WEEK_PRESETS.map(w => {
                const active = weeks === w
                return (
                  <button
                    key={w}
                    type="button"
                    onClick={() => handleWeeksChange(w)}
                    className="flex-1 h-11 rounded-xl text-sm font-semibold transition-colors"
                    style={active
                      ? { backgroundColor: theme.buttonColor, color: theme.buttonTextColor }
                      : {
                        backgroundColor: theme.cardBg,
                        border: `1px solid ${theme.cardBorder}`,
                        color: theme.text.primary,
                      }
                    }
                  >
                    {translate('trainingPrograms.assignProgram.weekChip', { count: w })}
                  </button>
                )
              })}
            </div>

            {selectedProgram && (
              <p className="text-xs text-center mb-4" style={{ color: theme.text.muted }}>
                {selectedProgram.name} · {startDate} → {endDate}
              </p>
            )}

            {error && (
              <div className="rounded-xl p-3 mb-4 bg-red-500/10 border border-red-500/25">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
          </>
        )}
      </div>

      {programs.length > 0 && (
        <div className="px-4 pb-8 pt-3 shrink-0" style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleAssign}
            disabled={loading || !selectedProgramId || endDate < startDate}
          >
            {loading ? t.trainingPrograms.assignProgram.assigning : t.trainingPrograms.assignProgram.assignButton}
          </Button>
        </div>
      )}
    </div>
  )
}
